import React, { useState } from 'react';
import { Home, Image, List, Calendar, MoreHorizontal, Store, Zap, Users, Crown, X, Sparkles } from 'lucide-react';

const MobileNav = ({ activeTab, setActiveTab, canManageUsers, userRole, t }) => {
  const [showMoreModal, setShowMoreModal] = useState(false);

  // Main tabs - always visible in bottom bar
  const mainTabs = [
    { id: 'dashboard', icon: Home, label: t('nav.dashboard') },
    { id: 'media', icon: Image, label: t('nav.media') },
    { id: 'playlists', icon: List, label: t('nav.playlists') },
    { id: 'scheduler', icon: Calendar, label: t('nav.scheduler') },
  ];

  // Additional tabs - shown in "More" modal
  const moreTabs = [];

  if (userRole === 'admin' || userRole === 'super_admin') {
  moreTabs.push(
    { id: 'marketplace', icon: Store, label: t('nav.marketplace') },
    { id: 'widgets', icon: Zap, label: t('nav.widgets') },
    { id: 'content-studio', icon: Sparkles, label: t('nav.contentStudio', 'Content Studio') }
  );
}

  if (canManageUsers) {
    moreTabs.push({ id: 'users', icon: Users, label: t('nav.users') });
  }

  if (userRole === 'super_admin') {
    moreTabs.push({ id: 'superadmin', icon: Crown, label: 'Super Admin' });
  }

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    setShowMoreModal(false);
  };

  return (
    <>
      {/* Bottom Navigation Bar - nur auf Mobile */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 z-40 safe-area-bottom">
        <div className="flex justify-around items-center h-16 pb-safe">
          {/* Main Tabs */}
          {mainTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`flex-1 flex flex-col items-center justify-center gap-1 py-2 transition-colors ${
                  isActive
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                <Icon size={24} strokeWidth={isActive ? 2.5 : 2} />
                <span className={`text-xs ${isActive ? 'font-semibold' : 'font-medium'}`}>
                  {tab.label}
                </span>
              </button>
            );
          })}

          {/* More Button - only if there are additional tabs */}
          {moreTabs.length > 0 && (
            <button
              onClick={() => setShowMoreModal(true)}
              className={`flex-1 flex flex-col items-center justify-center gap-1 py-2 transition-colors ${
                moreTabs.some(tab => tab.id === activeTab)
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              <MoreHorizontal size={24} strokeWidth={moreTabs.some(tab => tab.id === activeTab) ? 2.5 : 2} />
              <span className={`text-xs ${moreTabs.some(tab => tab.id === activeTab) ? 'font-semibold' : 'font-medium'}`}>
                More
              </span>
            </button>
          )}
        </div>
      </nav>

      {/* More Modal - Full Screen */}
      {showMoreModal && (
        <div className="md:hidden fixed inset-0 bg-black/50 dark:bg-black/70 z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-0 w-full max-w-md mx-auto mt-20 modal-container">
            
            {/* Header */}
            <div className="modal-header">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  More Options
                </h3>
                <button
                  onClick={() => setShowMoreModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="modal-content px-4">
              <div className="space-y-2">
                {moreTabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabClick(tab.id)}
                      className={`w-full text-left px-4 py-4 rounded-lg transition-colors flex items-center gap-3 ${
                        isActive
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <Icon size={24} strokeWidth={isActive ? 2.5 : 2} />
                      <span className="font-semibold text-base">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button
                onClick={() => setShowMoreModal(false)}
                className="w-full bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-4 py-3 rounded-lg transition-colors font-semibold"
              >
                Close
              </button>
            </div>
            
          </div>
        </div>
      )}
    </>
  );
};

export default MobileNav;